import i18n from "i18next";

/**
 * Sanitize raw Convex auth error messages into user-friendly strings.
 *
 * Convex errors often look like:
 *   [CONVEX A(auth:signIn)] [Request ID: ...] Server Error Uncaught Error: {"statusCode":403,...}
 *
 * This function extracts meaningful info and maps it to translated messages.
 */
export function sanitizeAuthError(
  rawMessage: string,
  flow: "signIn" | "signUp" | "otp" | "reset",
): string {
  const lower = rawMessage.toLowerCase();

  // Invalid credentials / wrong password
  if (
    lower.includes("invalid credentials") ||
    lower.includes("invalid password") ||
    lower.includes("could not verify")
  ) {
    return i18n.t("auth:errors.invalidCredentials");
  }

  // Account not found
  if (
    lower.includes("user not found") ||
    lower.includes("no user") ||
    lower.includes("account not found")
  ) {
    return i18n.t("auth:errors.accountNotFound");
  }

  // Account already exists
  if (
    lower.includes("already exists") ||
    lower.includes("already registered") ||
    lower.includes("duplicate")
  ) {
    return i18n.t("auth:errors.accountAlreadyExists");
  }

  // Rate limiting
  if (lower.includes("rate limit") || lower.includes("too many")) {
    return i18n.t("auth:errors.tooManyAttempts");
  }

  // Email sending / verification issues (like the Resend domain error)
  if (
    lower.includes("send") &&
    (lower.includes("email") || lower.includes("verification"))
  ) {
    return i18n.t("auth:errors.verificationEmailFailed");
  }

  // OTP errors
  if (lower.includes("invalid") && lower.includes("code")) {
    return i18n.t("auth:errors.invalidOtp");
  }

  if (lower.includes("expired")) {
    return i18n.t("auth:errors.otpExpired");
  }

  // Fallback to generic per-flow message
  switch (flow) {
    case "signIn":
      return i18n.t("auth:errors.signInFailed");
    case "signUp":
      return i18n.t("auth:errors.signUpFailed");
    case "otp":
      return i18n.t("auth:errors.invalidOtp");
    case "reset":
      return i18n.t("auth:errors.resetFailed");
  }
}
